var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useRef, useEffect, useCallback } from "react";
import SvgCanvas from "@svgedit/svgcanvas";
import { svgCanvasConfig } from "./svgCanvasConfig";
import updateCanvas from "./updateCanvas";
export const useCanvasSetup = (imageUrl) => {
    const svgCanvasRef = useRef(null);
    const svgCanvasInstanceRef = useRef(null);
    const createSvgWithImage = useCallback((url) => __awaiter(void 0, void 0, void 0, function* () {
        const isSvg = url.toLowerCase().endsWith(".svg") || url.startsWith("data:image/svg+xml");
        if (isSvg) {
            try {
                let svgContent;
                if (url.startsWith("data:image/svg+xml")) {
                    const decoded = decodeURIComponent(url.split(",")[1] || url);
                    svgContent = decoded;
                }
                else {
                    const response = yield fetch(url);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch SVG: ${response.statusText}`);
                    }
                    svgContent = yield response.text();
                }
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
                const svgElement = svgDoc.documentElement;
                if (svgElement.tagName === "svg") {
                    const viewBox = svgElement.getAttribute("viewBox");
                    let width = svgElement.getAttribute("width") || "800";
                    let height = svgElement.getAttribute("height") || "600";
                    width = width.replace(/px|em|rem|%/, "") || "800";
                    height = height.replace(/px|em|rem|%/, "") || "600";
                    const innerContent = Array.from(svgElement.children)
                        .filter((child) => {
                        const tagName = child.tagName.toLowerCase();
                        return tagName !== "title" && tagName !== "metadata" && tagName !== "desc";
                    })
                        .map((child) => child.outerHTML)
                        .join("");
                    let finalSvg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"`;
                    if (viewBox) {
                        finalSvg += ` viewBox="${viewBox}"`;
                    }
                    finalSvg += `>${innerContent}</svg>`;
                    return finalSvg;
                }
                return svgContent;
            }
            catch (error) {
                console.error("Error loading SVG:", error);
                return `<svg width="800" height="600" xmlns="http://www.w3.org/2000/svg"></svg>`;
            }
        }
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                const svg = `<svg width="800" height="600" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <image x="0" y="0" width="800" height="600" xlink:href="${url}" preserveAspectRatio="xMidYMid meet" />
        </svg>`;
                resolve(svg);
            };
            img.onerror = () => {
                const svg = `<svg width="800" height="600" xmlns="http://www.w3.org/2000/svg"></svg>`;
                resolve(svg);
            };
            img.src = url;
        });
    }), []);
    useEffect(() => {
        if (!svgCanvasRef.current)
            return;
        let isMounted = true;
        const initCanvas = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!isMounted || !svgCanvasRef.current)
                return;
            try {
                const initialSvg = yield createSvgWithImage(imageUrl);
                const config = Object.assign({}, svgCanvasConfig);
                const canvas = new SvgCanvas(svgCanvasRef.current, config);
                svgCanvasInstanceRef.current = canvas;
                const success = canvas.setSvgString(initialSvg.replace(/'/g, "\\'"), true);
                if (!success) {
                    console.error("Error loading SVG");
                    return;
                }
                updateCanvas({
                    svgCanvas: canvas,
                    cnvs: svgCanvasRef.current,
                    curConfig: config,
                    center: true,
                });
                setTimeout(() => {
                    canvas.setMode("select");
                    const svgRoot = canvas.getSvgRoot();
                    if (svgRoot) {
                        svgRoot.setAttribute("style", "pointer-events: none;");
                        let idCounter = 0;
                        const applyCursorStyles = (element, index) => {
                            var _a;
                            if (element.tagName === "image")
                                return;
                            if (!element.getAttribute("id")) {
                                element.setAttribute("id", `${((_a = element.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) || "element"}-${Date.now()}-${index}-${idCounter++}`);
                            }
                            const style = element.getAttribute("style") || "";
                            if (!style.includes("pointer-events")) {
                                element.setAttribute("style", `${style}; pointer-events: auto;`.trim());
                            }
                            const sw = element.getAttribute("stroke-width");
                            if (sw) {
                                const w = parseFloat(sw);
                                if (w > 0 && w < 10)
                                    element.setAttribute("stroke-width", Math.max(w, 3).toString());
                            }
                        };
                        Array.from(svgRoot.children).forEach((child, index) => {
                            if (child instanceof SVGElement) {
                                applyCursorStyles(child, index);
                            }
                        });
                        const updateFontFamily = () => {
                            const root = canvas.getSvgRoot();
                            if (!root)
                                return;
                            root.querySelectorAll("text").forEach((textElem) => {
                                if (textElem instanceof SVGTextElement) {
                                    const fontFamily = textElem.getAttribute("font-family");
                                    if (!fontFamily || fontFamily === "serif" || fontFamily.toLowerCase().includes("times")) {
                                        textElem.setAttribute("font-family", "Roboto");
                                    }
                                }
                            });
                        };
                        const updateCursorStyles = () => {
                            const root = canvas.getSvgRoot();
                            if (!root)
                                return;
                            Array.from(root.children).forEach((child, index) => {
                                if (child instanceof SVGElement)
                                    applyCursorStyles(child, index);
                            });
                            root.querySelectorAll("line, path, rect, circle, ellipse, polyline, polygon").forEach((elem) => {
                                if (elem instanceof SVGElement && elem.parentElement !== root) {
                                    const style = elem.getAttribute("style") || "";
                                    if (!style.includes("pointer-events")) {
                                        elem.setAttribute("style", `${style}; pointer-events: auto;`.trim());
                                    }
                                }
                            });
                        };
                        updateCursorStyles();
                        updateFontFamily();
                        canvas.bind("changed", updateCursorStyles);
                    }
                }, 100);
                canvas.bind("selected", (_win, elems) => {
                    const svgRoot = canvas.getSvgRoot();
                    if (!svgRoot)
                        return;
                    if ((elems === null || elems === void 0 ? void 0 : elems.length) > 0) {
                        if (elems.some((e) => { var _a; return e === svgRoot || ((_a = e.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === "svg"; })) {
                            setTimeout(() => canvas.clearSelection(), 0);
                            document.dispatchEvent(new CustomEvent("selectionchanged", { detail: { elements: [] } }));
                            return;
                        }
                        const validElements = elems.filter((e) => {
                            var _a;
                            if (!(e instanceof SVGElement))
                                return false;
                            const tag = (_a = e.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase();
                            return e !== svgRoot && tag !== "svg" && tag !== "image";
                        });
                        document.dispatchEvent(new CustomEvent("selectionchanged", { detail: { elements: validElements } }));
                    }
                    else {
                        document.dispatchEvent(new CustomEvent("selectionchanged", { detail: { elements: [] } }));
                    }
                });
            }
            catch (error) {
                console.error("Error initializing canvas:", error);
            }
        });
        requestAnimationFrame(() => {
            if (isMounted) {
                initCanvas();
            }
        });
        return () => {
            isMounted = false;
            if (svgCanvasInstanceRef.current) {
                svgCanvasInstanceRef.current = null;
            }
        };
    }, [imageUrl, createSvgWithImage]);
    return { svgCanvasRef, svgCanvasInstanceRef };
};
